import type {QueryParams} from '@jetbrains/teamcity-ui/dist/utils'

import {loadBuildsData} from '../../../../actions/builds'
import type {AppThunk} from '../../../../actions/types'
import {getLocator} from '../../../../rest/locators'
import {getTabsArg} from '../../../../rest/tabs'
import type {LoadedEndpoints} from '../../../../routes/loadEndpoint'
import {loadEndpoint} from '../../../../routes/loadEndpoint'
import {restApi} from '../../../../services/rest'
import type {BuildId} from '../../../../types'
import {BuildPageTabNamesEnum, ChangesType} from '../../../../types'
import {parseBuildFilters} from '../../../../utils/queryParams'
import {getTabParamsKey} from '../../../../utils/tabs'
import {preloadArtifactChanges} from '../../../common/ArtifactChanges/ArtifactChanges.preload'
import {preloadBuildArtifactsTree} from '../../../common/BuildArtifactsTree/BuildArtifactsTree.preload'
import {preloadBuildPartOf} from '../../../common/BuildInfoTable/BuildPartOf/BuildPartOf.preload'
import {SnapshotDependenciesModes} from '../DependenciesTab/DependenciesTab.modes'
import {getSnapshotDependenciesLocator} from '../DependenciesTab/DependenciesTab.utils'

export const preloadBuildTabs =
  (
    buildId: BuildId,
    buildTab: string | null | undefined,
    {
      mode = SnapshotDependenciesModes.TIMELINE,
      state,
      pinned,
      starred,
      changesType,
      showAll,
    }: QueryParams,
    isPipeline?: boolean,
  ): AppThunk<LoadedEndpoints> =>
  dispatch => {
    const preloadedEndpoints: LoadedEndpoints = []
    switch (buildTab) {
      case BuildPageTabNamesEnum.DEPENDENCIES:
        const {
          buildState: parsedState,
          pinned: isPinned,
          starred: isStarred,
        } = parseBuildFilters(state, pinned, starred)
        const withRunningAndQueued =
          parsedState.length === 0 || parsedState.some(s => s === 'running' || s === 'queued')
        const dependenciesLocator = getLocator({
          baseLocator: getSnapshotDependenciesLocator(buildId, mode),
          withRunningAndQueued,
          buildState: parsedState,
          pinned: isPinned,
          starred: isStarred,
        })

        if (dependenciesLocator != null) {
          preloadedEndpoints.push(
            ...dispatch(
              loadBuildsData({
                isPreload: true,
                locator: dependenciesLocator,
                requestOptions: {
                  withBuildTypeDetails: true,
                  withSnapshotDependencies: true,
                  withQueuedInfo: withRunningAndQueued,
                  withRunningInfo: withRunningAndQueued,
                  withBuildDetails: mode === SnapshotDependenciesModes.LIST,
                  essential: true,
                },
              }),
            ),
          )
        }
        break
      case BuildPageTabNamesEnum.CHANGES:
        if (changesType === ChangesType.ARTIFACTS) {
          preloadedEndpoints.push(...dispatch(preloadArtifactChanges(buildId)))
        }
        break
      case BuildPageTabNamesEnum.ARTIFACTS:
        preloadedEndpoints.push(...dispatch(preloadBuildArtifactsTree(buildId, showAll === 'true')))
        break
      case BuildPageTabNamesEnum.OVERVIEW:
      default:
        if (!isPipeline) {
          preloadedEndpoints.push(...dispatch(preloadBuildPartOf(buildId)))
        }
    }

    const tabParamsKey = getTabParamsKey({buildId})
    preloadedEndpoints.push(
      dispatch(loadEndpoint(restApi.endpoints.getTabs, getTabsArg(tabParamsKey, false))),
    )
    return preloadedEndpoints
  }
